import { FC, ReactNode, useEffect } from 'react'
import styled from 'styled-components'

/**
 * Invólucro de modal compartilhado — o "chassi" visual do comparativo
 * (analytics/comparativo, SectorMappingModal): overlay escuro que rola a
 * página, painel branco arredondado com sombra, cabeçalho com título/subtítulo
 * e botão de fechar, e um rodapé opcional. O miolo (children) fica a cargo de
 * cada tela. Fecha ao clicar fora, no X ou com ESC, e trava o scroll do body.
 */

const BORDER = '#EAECF0'
const INK = '#212121'
const FAINT = '#B0B0B0'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(16, 24, 40, 0.55);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;

  @media (max-width: 600px) {
    padding: 16px 8px;
  }
`

const Panel = styled.div<{ $maxWidth: number }>`
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 16px;
  width: 100%;
  max-width: ${(p) => p.$maxWidth}px;
  box-shadow: 0 24px 60px -20px rgba(16, 24, 40, 0.4);
  overflow: hidden;
`

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid ${BORDER};
`

const HeadText = styled.div`
  min-width: 0;
`

const Title = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${INK};
`

const Sub = styled.div`
  font-size: 12px;
  color: ${FAINT};
  margin-top: 2px;
`

const X = styled.button`
  margin-left: auto;
  align-self: flex-start;
  border: 0;
  background: transparent;
  font-size: 20px;
  color: ${FAINT};
  cursor: pointer;
  line-height: 1;
`

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid ${BORDER};
`

interface ModalShellProps {
  title: ReactNode
  subtitle?: ReactNode
  onClose: () => void
  children: ReactNode
  /** Conteúdo do rodapé (ex.: nota + botões). Omitido = sem rodapé. */
  footer?: ReactNode
  /** Largura máxima do painel em px. Default 900. */
  maxWidth?: number
  /** Se false, clicar no overlay não fecha (só o X / ESC). Default true. */
  closeOnOverlayClick?: boolean
}

const ModalShell: FC<ModalShellProps> = ({
  title,
  subtitle,
  onClose,
  children,
  footer,
  maxWidth = 900,
  closeOnOverlayClick = true,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [onClose])

  return (
    <Overlay onClick={closeOnOverlayClick ? onClose : undefined}>
      <Panel $maxWidth={maxWidth} onClick={(e) => e.stopPropagation()}>
        <Head>
          <HeadText>
            <Title>{title}</Title>
            {subtitle && <Sub>{subtitle}</Sub>}
          </HeadText>
          <X onClick={onClose} aria-label="Fechar">
            ✕
          </X>
        </Head>

        {children}

        {footer && <Foot>{footer}</Foot>}
      </Panel>
    </Overlay>
  )
}

export default ModalShell
